import React from "react";
import autobind from "class-autobind";
import strings from "../../../localization/strings";
import { Paper, CircularProgress, Card } from "material-ui";
import * as api from "../../../api";
import { IDate } from "../../../models";
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore";
import moment from "moment";
import { ETrendTimeScope, IGroupedTrend, ITrendSearch } from "../../../models/data/dashboard/Trends/TrendsData";
import TrendSummaryPanel from "./TrendSummaryPanel";
import TrendDetails from "./TrendDetails";

declare global {
    interface Window {
        Intercom: any;
    }
}

interface IProps {}

interface IState {
    loading: boolean;
    firstLoad: boolean;
    calculating: boolean;
    startDate: IDate;
    endDate: IDate;
    scope: ETrendTimeScope;
    data: ITrendSearch;
    selectedTrendSettingsIds: string[]; // Array to store selected TrendSettingsIds
    selectedGroups: IGroupedTrend[];
}

export default class TrendsDashboard extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);

        const { calcualteStartDateLocalUTCFormat, calcualteEndDateLocalUTCFormat } = this.getCalcualteStartEndDateLocalUTCFormat();

        this.state = {
            loading: true,
            firstLoad: true,
            calculating: false,
            startDate: localStorage.getItem("findd_trends_startDate") && localStorage.getItem("findd_trends_startDate") !== "null"
                ? moment(localStorage.getItem("findd_trends_startDate")).toDate()
                : calcualteStartDateLocalUTCFormat,
            endDate: localStorage.getItem("findd_trends_endDate") && localStorage.getItem("findd_trends_endDate") !== "null"
                ? moment(localStorage.getItem("findd_trends_endDate")).toDate()
                : calcualteEndDateLocalUTCFormat,
            scope: localStorage.getItem("findd_trends_scope") ? Number.parseInt(localStorage.getItem("findd_trends_scope")) : ETrendTimeScope.Weekly,
            data: null,
            selectedTrendSettingsIds: JSON.parse(localStorage.getItem("findd_trends_selectedTrendSettingsIds")) || [], // Restore selected TrendSettingsIds from localStorage
            selectedGroups: [],
        };

        autobind(this);
    }

    getCalcualteStartEndDateLocalUTCFormat() {
        let calcualteStartDateLocalUTCFormat = null;
        let calcualteEndDateLocalUTCFormat = null;

        calcualteStartDateLocalUTCFormat = moment().startOf("year").utc(true).format();
        calcualteEndDateLocalUTCFormat = moment().utc(true).format();

        return { calcualteStartDateLocalUTCFormat, calcualteEndDateLocalUTCFormat };
    }

    updateDateRange(newStartDate: IDate, newEndDate: IDate) {
        newStartDate = moment(newStartDate).startOf("day").toDate();
        newEndDate = moment(newEndDate).endOf("day").toDate();
        localStorage.setItem("findd_trends_startDate", newStartDate.toISOString());
        localStorage.setItem("findd_trends_endDate", newEndDate.toISOString());
        this.setState({
            startDate: newStartDate,
            endDate: newEndDate
        }, this.fetchData); // Fetch data after updating state
    }

    componentDidMount() {
        document.title = `${strings.Dashboard} | Trend Analysis`;
        HierarchySettingsStore.on("global_change", this.fetchData);
        this.fetchData();
    }

    componentWillUnmount() {
        HierarchySettingsStore.off("global_change", this.fetchData);
    }

    fetchData() {
        if (!this.state.loading || this.state.firstLoad) {
            this.setState({ firstLoad: false, loading: true }, () => {
                api.trendSearch(
                    HierarchySettingsStore.getGlobalLevelItemFilterId(),
                    moment(this.state.startDate).toISOString(true),  // Start date
                    moment(this.state.endDate).toISOString(true),    // End date
                    this.state.scope
                ).then((result) => {
                    let selectedGroups = [];
                    let filterHierarchyId = HierarchySettingsStore.getGlobalLevelItemFilterId();

                    if (result.data) {
                        if (filterHierarchyId != null && filterHierarchyId === "") {
                            filterHierarchyId = null;
                        }

                        // Filter the selected groups based on the saved TrendSettingsIds
                        selectedGroups = result.data.GroupedTrends.filter(
                            (item) => item.HierarchyId === filterHierarchyId && this.state.selectedTrendSettingsIds.includes(item.Settings.Id) && !item.LeafNode
                        );
                    }

                    this.setState({
                        data: result.data,
                        loading: false,
                        selectedGroups: selectedGroups
                    });
                });
            });
        }
    }

    handleScopeChange(newScope: ETrendTimeScope) {
        this.setState({ scope: newScope }, () => {
            localStorage.setItem("findd_trends_scope", newScope.toString());
            this.fetchData();
        });
    }

    handleSelectNewItem(newItem: IGroupedTrend) {
        this.setState((prevState) => {
            const isAlreadySelected = prevState.selectedTrendSettingsIds.includes(newItem.Settings.Id);

            const newSelectedTrendSettingsIds = isAlreadySelected
                ? prevState.selectedTrendSettingsIds.filter(id => id !== newItem.Settings.Id)
                : [...prevState.selectedTrendSettingsIds, newItem.Settings.Id];

            localStorage.setItem("findd_trends_selectedTrendSettingsIds", JSON.stringify(newSelectedTrendSettingsIds));

            const newSelectedGroups = isAlreadySelected
                ? prevState.selectedGroups.filter(group => group.Settings.Id !== newItem.Settings.Id)
                : [...prevState.selectedGroups, newItem];                
            return { selectedTrendSettingsIds: newSelectedTrendSettingsIds, selectedGroups: newSelectedGroups };
        });
    }

    render() {
        const { data, loading, selectedGroups, scope, startDate, endDate, selectedTrendSettingsIds } = this.state;

        return (
            <Paper>
                {loading &&
                    <div className="spinner">
                        <CircularProgress />
                    </div>
                }

                {!loading && (
                    <div style={{ display: "flex" }}>
                        <div style={{ flex: 1 }}>
                            <TrendSummaryPanel
                                scope={scope}
                                data={data!}
                                selectedIds = {selectedTrendSettingsIds}
                                onSelectNewItem={this.handleSelectNewItem}
                            />
                        </div>
                        <Card style={{ flex: 2, borderTop: "solid 1px #eaeaea" }}>
                            <TrendDetails 
                                selectedGroups={selectedGroups} 
                                scope={scope}
                                trendSearch={data!} 
                                onScopeChange={this.handleScopeChange}
                                startDate={startDate} 
                                endDate={endDate} 
                                updateDateRange={this.updateDateRange} 
                                onSelectNewItem={this.handleSelectNewItem}
                            />
                        </Card>
                    </div>
                )}
            </Paper>
        );
    }
}
